import { type ReactNode, useId } from 'react'
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group'

import { cn } from '@gto/ui/cn'
import { typography } from '@gto/ui/typography'

type RadioGroupProps = {
  /** Controlled selected value */
  value?: string
  /** Initial selected value for uncontrolled mode */
  defaultValue?: string
  /** Callback fired when the selected value changes */
  onValueChange?: (value: string) => void
  /** Whether the entire group is disabled */
  disabled?: boolean
  /** Whether the group is in an error state (colors label and helper text red) */
  error?: boolean
  /** Whether the group is required — shows an asterisk next to the label */
  required?: boolean
  /** Form field name shared across all radios */
  name?: string
  /** Group label displayed above the radios */
  label?: ReactNode
  /** Helper text displayed below the radios */
  helperText?: ReactNode
  /** Arrange radios in a horizontal row */
  row?: boolean
  /** Additional CSS classes */
  className?: string
  children: ReactNode
}

const RadioGroup = ({
  value,
  defaultValue,
  onValueChange,
  disabled = false,
  error = false,
  required = false,
  name,
  label,
  helperText,
  row = false,
  className,
  children,
}: RadioGroupProps) => {
  const labelId = useId()
  const ariaLabelledBy = label === null || label === undefined ? undefined : labelId

  return (
    <div
      className={cn('flex flex-col gap-2', className)}
      role="group"
    >
      {label != null && (
        <span
          id={labelId}
          className={cn(
            typography['input/label'],
            'text-text-secondary',
            error && 'text-error-main',
            disabled && 'text-text-disabled',
          )}
        >
          {label}
          {required && <span className="ml-1 text-error-main">*</span>}
        </span>
      )}

      <BaseRadioGroup
        aria-labelledby={ariaLabelledBy}
        className={cn(row ? 'flex flex-row gap-8' : 'flex flex-col gap-4')}
        defaultValue={defaultValue}
        disabled={disabled}
        name={name}
        required={required}
        value={value}
        onValueChange={onValueChange}
      >
        {children}
      </BaseRadioGroup>

      {helperText != null && (
        <span
          className={cn(
            typography['input/helper'],
            'text-text-secondary',
            error && 'text-error-main',
            disabled && 'text-text-disabled',
          )}
        >
          {helperText}
        </span>
      )}
    </div>
  )
}

export { RadioGroup }
export type { RadioGroupProps }
